import { DashboardCard } from '@/components/ui/dashboard-card.tsx';
import {
  TGetDeviceResponse,
  useGetDeviceQuery,
} from '@/features/devices/hooks/queries/[device]/get-device.ts';
import { cn, formatValueOrDefault } from '@/utils/utils.ts';
import {
  TBMSDeviceStatus,
  useGetBMSStatusDeviceQuery,
} from '@/features/devices/hooks/queries/[device]/get-bms-status-device.ts';
import BmsDeviceWhiteImage from '@/assets/bms-device-white-image.png';
import { useTranslation, UseTranslationResponse } from 'react-i18next';

export function BatteryStatsCard({
  deviceId,
  className,
}: {
  deviceId: string | undefined;
  className: string | undefined;
}) {
  const { data: device, isLoading: isDeviceLoading } = useGetDeviceQuery({
    deviceId: deviceId ?? '',
  });
  const { data: deviceStatus } = useGetBMSStatusDeviceQuery({
    deviceId,
    moduleNumber: undefined,
  });
  const { t } = useTranslation();

  return (
    <DashboardCard
      isLoading={isDeviceLoading}
      className={cn(className, 'h-min min-h-[600px] w-full gap-5 pb-4')}
    >
      <div className="flex justify-center">
        <img
          src={BmsDeviceWhiteImage}
          alt={t('devices.details.bmsImage')}
          className="h-64 max-w-[350px]"
        />
      </div>
      <div className="flex flex-col gap-5">
        <InfoSection title={t('devices.details.title')} items={getBMSDetails(t, device)} />
        <InfoSection
          title={t('devices.details.status')}
          items={getDeviceStatus(t, deviceStatus)}
        />
      </div>
    </DashboardCard>
  );
}

function getBMSDetails(
  t: UseTranslationResponse<'translation', undefined>['t'],
  device?: TGetDeviceResponse
) {
  return [
    { label: t('devices.details.serialNumber'), value: device?.sn },
    { label: t('devices.details.supplier'), value: device?.supplier },
    { label: t('devices.details.plantName'), value: device?.plantName },
    { label: t('devices.details.location'), value: device?.plantCity },
    { label: t('devices.details.connectedAt'), value: device?.connectedAt },
  ];
}

function getDeviceStatus(
  t: UseTranslationResponse<'translation', undefined>['t'],
  status?: TBMSDeviceStatus
) {
  return [
    { label: t('devices.select.parameters.modbusStatus'), value: status?.statusModbus },
    { label: t('devices.select.parameters.statusError'), value: status?.statusError },
    { label: t('devices.select.parameters.statusIdle'), value: status?.statusIdle },
    { label: t('devices.select.parameters.statusCharge'), value: status?.statusCharge },
    { label: t('devices.select.parameters.statusDischarge'), value: status?.statusDischarge },
  ];
}

const InfoSection = ({
  title,
  items,
}: {
  title: string;
  items: {
    label: string;
    value: number | string | null | undefined;
  }[];
}) => (
  <div className="flex flex-col gap-1">
    <h3 className="text-lg font-medium">{title}</h3>
    <ul className="grid grid-cols-2 gap-3 sm:grid-cols-3">
      {items.map(({ label, value }) => (
        <li key={label} className="flex flex-col">
          <span className="text-muted-foreground text-sm text-nowrap">{label}</span>
          <span className="line-clamp-2 font-medium text-ellipsis">
            {formatValueOrDefault(value)}
          </span>
        </li>
      ))}
    </ul>
  </div>
);
